import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';

import gettext from 'js-utils/gettext';
import { setWindowLocation } from 'js-utils/http';
import Button from 'eventbrite_design_system/button/Button';
import {
    TYPE_ERROR,
    TYPE_WARNING,
} from 'eventbrite_design_system/notification/constants';
import EmptyState from 'eventbrite_design_system/emptyState/EmptyState';
import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';
import NotificationBar from 'eventbrite_design_system/notification/NotificationBar';
import PhabButton from 'eventbrite_design_system/phab/PhabButton';
import ProgressIndicator from 'eventbrite_design_system/progressIndicator/ProgressIndicator';
import SelectField from 'eventbrite_design_system/inputField/SelectField';
import SearchFilter from 'eventbrite_design_system/searchFilter/SearchFilter';
import CalendarGraphicSvg from 'eventbrite_design_system/iconography/icons/CalendarGraphic';
import PlusChunkySvg from 'eventbrite_design_system/iconography/icons/PlusChunky';
import { ENV_SHAPE_PROP_TYPE } from '@eb/site-structure';

import {
    DEFAULT_SEARCH_QUERY,
    DEFAULT_PAGE_NUMBER,
    DEFAULT_PAGE_SIZE,
    DEFAULT_ORGANIZER_ID,
    EVENT_SHAPE,
    STATUS_FILTERS,
    SELECT_FIELD_FILTER_OPTIONS,
    STATUS_FILTER_PROP_TYPE,
    STATUS_FILTER_DRAFT,
    STATUS_FILTER_PAST,
    STATUS_FILTER_PUBLISHED,
    STATUS_FILTER_ALL,
    STATUS_COUNTS_PUBLISHED,
    STATUS_COUNTS_DRAFT,
    STATUS_COUNTS_PAST,
    ORGANIZER_SHAPE,
} from '../constants';
import EventsList from './EventsList';

import './eventsScreen.scss';

const Header = () => (
    <div className="eds-show-up-md eds-g-grid eds-align--center-vertical eds-l-mar-top-10">
        <div className="eds-g-cell eds-g-cell-8-12">
            <h1 className="eds-text-hm" data-spec="events-screen-heading">
                {gettext('Events')}
            </h1>
        </div>
        <div className="eds-g-cell eds-g-cell-4-12">
            <div className="eds-align--right">
                <Button style="fill" type="link" href="/create">
                    {gettext('Create Event')}
                </Button>
            </div>
        </div>
    </div>
);

const getHTMLFromText = (htmlMessage) => (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />
);

const InvoiceDueNotification = ({ amount, helpLink }) => {
    const message = gettext(
        'Your invoice for %(amount)s is due today. <a href="/invoices">Pay now</a> or <a href="%(helpLink)s" target="_blank" rel="noopener noreferrer">learn more about invoices</a>.',
        { amount, helpLink },
    );

    return <NotificationBar>{getHTMLFromText(message)}</NotificationBar>;
};

export default class EventsScreen extends PureComponent {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        organizationId: PropTypes.string.isRequired,
        status: STATUS_FILTER_PROP_TYPE.isRequired,
        onLoadEvents: PropTypes.func.isRequired,
        onFilterEvents: PropTypes.func.isRequired,
        onLoadStatusCounts: PropTypes.func,
        onLoadOrganizers: PropTypes.func,
        addMainNotification: PropTypes.func,
        events: PropTypes.arrayOf(EVENT_SHAPE),
        organizers: PropTypes.arrayOf(ORGANIZER_SHAPE),
        searchQuery: PropTypes.string,
        currentPage: PropTypes.number,
        pageSize: PropTypes.number,
        pageCount: PropTypes.number,
        organizerId: PropTypes.string,
        isEmbedded: PropTypes.bool,
        crumb: PropTypes.string,
        user: PropTypes.object,
    };

    static defaultProps = {
        searchQuery: DEFAULT_SEARCH_QUERY,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_PAGE_SIZE,
        pageCount: 1,
        organizerId: DEFAULT_ORGANIZER_ID,
        isEmbedded: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.searchQuery,
        };
    }
    componentDidMount() {
        this._isMounted = true;

        // if we're doing SSR and if we are not passing in an onLoadStatusCounts
        // functions then skip the status count call since it won't be used
        if (!this._isSSR() && this.props.onLoadStatusCounts) {
            this._loadStatusCounts();
        }

        this._loadEvents();
        this._loadOrganizers();
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(prevProps.events, this.props.events)) {
            this._loadEvents();
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            this.props.onLoadStatusCounts &&
            !isEqual(nextProps.statusCounts, this.props.statusCounts)
        ) {
            this._getDefaultStatusFilter(nextProps.statusCounts);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isSSR() {
        return !!STATUS_FILTERS.find((status) =>
            this.props.request.path.includes(status),
        );
    }

    _showFailureNotification(message) {
        const { addMainNotification } = this.props;

        if (addMainNotification) {
            addMainNotification({
                children: message,
                type: TYPE_ERROR,
                shouldScrollTo: true,
                iconType: <AlertChunkySvg />,
                hasCloseButton: true,
            });
        }
    }

    _getDefaultStatusFilter(statusCounts) {
        // get only the statuses that have a count greater than 0
        const filteredStatuses = Object.keys(statusCounts).filter(
            (statusType) => statusCounts[statusType] > 0,
        );

        let defaultStatus;

        // if filteredStatuses is empty then every status is at zero so
        // default to All
        if (isEmpty(filteredStatuses)) {
            defaultStatus = STATUS_FILTER_ALL;
        }

        // otherwise we want to go in order of
        // published -> draft -> past -> all
        else if (filteredStatuses.includes(STATUS_COUNTS_PUBLISHED)) {
            defaultStatus = STATUS_FILTER_PUBLISHED;
        } else if (filteredStatuses.includes(STATUS_COUNTS_DRAFT)) {
            defaultStatus = STATUS_FILTER_DRAFT;
        } else if (filteredStatuses.includes(STATUS_COUNTS_PAST)) {
            defaultStatus = STATUS_FILTER_PAST;
        }

        // set the filter to the correct status
        this._handleStatusFilter(defaultStatus);
    }

    _showMaintenanceBanner = () => (
        <NotificationBar
            type={TYPE_WARNING}
            onCallAction={() => null}
            callAction={
                <a href="https://www.eventbritestatus.com/">
                    {gettext('View status updates')}
                </a>
            }
        >
            {gettext(
                'Eventbrite will be temporarily unavailable for scheduled maintenance on Saturday, March 21st starting at 8am Pacific Time (3pm UTC) for approximately 15 minutes. We appreciate your understanding and patience',
            )}
        </NotificationBar>
    );

    _loadEvents = () => {
        const {
            events,
            onLoadEvents,
            organizationId,
            status,
            searchQuery,
            pageNumber,
            pageSize,
            organizerId,
        } = this.props;

        // NOTE: Undefined `events` signifies that it hasn't
        // been loaded yet, which would be different from empty
        // which signifies that they have been loaded but there
        // aren't any.
        if (!events && this._isMounted) {
            onLoadEvents(organizationId, {
                status,
                searchQuery,
                pageNumber,
                pageSize,
                organizerId,
            }).catch(() =>
                this._showFailureNotification(
                    gettext(
                        'There was an error accessing this page. Please check the url address and try again.',
                    ),
                ),
            );
        }
    };

    _loadOrganizers = () => {
        const { organizers, onLoadOrganizers, organizationId } = this.props;

        // NOTE: Undefined `organizers` signifies that it hasn't
        // been loaded yet, which would be different from empty
        // which signifies that they have been loaded but there
        // aren't any.
        if (!organizers && this._isMounted && onLoadOrganizers) {
            onLoadOrganizers(organizationId);
        }
    };

    _loadStatusCounts = () => {
        const { organizationId, onLoadStatusCounts, statusCounts } = this.props;

        // only get status counts if it's undefined or they are empty.
        if (!statusCounts || isEmpty(statusCounts)) {
            onLoadStatusCounts(organizationId);
        }
    };

    _handleFilter = (filterParams) => {
        const {
            organizationId,
            onFilterEvents,
            status,
            pageSize,
            organizerId,
        } = this.props;
        const { searchQuery } = this.state;
        const hasSearchFilter = has(filterParams, 'searchQuery');
        // If a searchQuery is passed as a filterParam we need to honor it as in the case
        // when the search field is cleared, otherwise we use the query from state
        const filterSearchTerm = hasSearchFilter
            ? filterParams.searchQuery
            : searchQuery;

        // NOTE: Effectively what will happen is that `onFilterEvents` will do a client-side
        // redirect to the events page w/ the updated filterParams. This will trigger
        // componentDidUpdate above, which will cause new events matching the params to load.
        // This makes the route the source of truth
        onFilterEvents(organizationId, {
            status,
            pageSize,
            pageNumber: DEFAULT_PAGE_NUMBER,
            searchQuery: filterSearchTerm,
            organizerId,
            ...filterParams,
        });

        if (hasSearchFilter) {
            // Update state with the searchQuery from filterParams to handle
            // the case when the search field is cleared
            this._handleSearchChange({ searchQuery: filterParams.searchQuery });
        }
    };

    _handlePageChange = (pageNumber) => {
        this._handleFilter({ pageNumber });
    };

    _handleStatusFilter = (status) => {
        this._handleFilter({ status });
    };

    _handleOrganizerFilter = (organizerId) => {
        this._handleFilter({ organizerId });
    };

    _handleSearchChange = ({ searchQuery }) => {
        this.setState({ searchQuery });
    };

    _getCmsNotifications = () => {
        const { cmsContent: { alert } = {}, isEmbedded } = this.props;
        let notifications;

        // If we are embedded in the manage's eventsScreen
        // we need to hide this banner
        if (alert && !isEmbedded) {
            const alertContent = (
                <NotificationBar
                    type={TYPE_ERROR}
                    iconType={<AlertChunkySvg />}
                    hasCloseButton={true}
                    data-spec="events-screen-cms-alert"
                >
                    {getHTMLFromText(alert)}
                </NotificationBar>
            );

            notifications = (
                <div className="eds-g-cell eds-g-cell-1-1 eds-l-mar-top-1">
                    {alertContent}
                </div>
            );
        }

        return notifications;
    };

    _getInvoiceNotifications = () => {
        const { invoiceDue, isEmbedded } = this.props;

        // If we are embedded in the manage's eventsScreen
        // we need to hide this banner
        let invoiceDueNotification;
        if (invoiceDue && !isEmbedded) {
            invoiceDueNotification = (
                <InvoiceDueNotification
                    amount={invoiceDue.amount}
                    helpLink={invoiceDue.helpLink}
                    data-spec="invoice-due-notification"
                />
            );
        }

        return invoiceDueNotification;
    };

    _renderOrganizerFilter = () => {
        const { organizerId, organizers } = this.props;

        // We only need to display the filter if there is more than one organizer
        // to filter on
        if (!organizers || organizers.length < 2) {
            return null;
        }

        const organizerValues = organizers.map(({ id, name }) => ({
            value: id,
            display: name || gettext('Unnamed Organizer'),
        }));
        const organizerSelectValues = [
            { value: DEFAULT_ORGANIZER_ID, display: gettext('All') },
            ...organizerValues,
        ];

        return (
            <div
                className="event-screen-selected-field-filter eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-4-12 eds-l-sn-pad-left-0 eds-l-sm-pad-left-0 eds-l-pad-left-2"
                data-spec="event-screen-organizer-filter-shell"
            >
                <SelectField
                    id="event-organizer-filter"
                    label={gettext('Organizer')}
                    value={organizerId}
                    values={organizerSelectValues}
                    onChange={this._handleOrganizerFilter}
                    defaultValue={DEFAULT_ORGANIZER_ID}
                    borderType="default"
                />
            </div>
        );
    };

    _renderContent = () => {
        const {
            env,
            events,
            status,
            pageNumber,
            pageCount,
            crumb,
            isExternalContentLoading,
            user,
        } = this.props;

        if (!events || isExternalContentLoading) {
            return (
                <div data-spec="events-screen-loading">
                    <ProgressIndicator shape="linear" style="gradient" />
                </div>
            );
        }

        if (!isEmpty(events)) {
            return (
                <EventsList
                    serverUrl={env.serverUrl}
                    events={events}
                    currencyFormat={env.currencyFormat}
                    statusFilter={status}
                    pageNumber={pageNumber}
                    pageCount={pageCount}
                    onPageChange={this._handlePageChange}
                    crumb={crumb}
                    user={user}
                />
            );
        }

        return (
            <div
                className="eds-l-mar-top-10"
                data-spec="events-screen-empty-state"
            >
                <EmptyState
                    graphicType={<CalendarGraphicSvg />}
                    description={gettext('No events to show')}
                />
            </div>
        );
    };

    render() {
        const { status, isEmbedded } = this.props;
        const { searchQuery } = this.state;
        const organizerFilter = this._renderOrganizerFilter();
        const searchFilterColumns = organizerFilter ? 4 : 6;
        let header;
        let createPhab;
        let className;

        if (!isEmbedded) {
            header = <Header />;
            createPhab = (
                <div className="eds-show-down-mn events-phab-container">
                    <PhabButton
                        name="create-event"
                        style="fill"
                        iconType={<PlusChunkySvg />}
                        onClick={setWindowLocation.bind(null, '/create')}
                    />
                </div>
            );
            className = 'eds-l-mar-bot-20 eds-l-pad-bot-20';
        }

        // NOTE: Need to leave enough space at the bottom so that the last couple of more action palettes can fit on the screen
        // Need to use both padding and margin to get enough space
        return (
            <div className={className} data-spec="events-screen">
                {this._getCmsNotifications()}
                {this._getInvoiceNotifications()}
                {header}
                <div className="eds-l-mar-vert-8">
                    <div className="eds-g-grid">
                        <div
                            className={`eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-${searchFilterColumns}-12`}
                            data-spec="events-screen-search-filter-shell"
                        >
                            <SearchFilter
                                searchQuery={searchQuery}
                                onChange={this._handleSearchChange}
                                onFilter={this._handleFilter}
                                inputLabel={gettext('Search events')}
                                inputPlaceholder={gettext('Search events')}
                            />
                        </div>
                        <div
                            className="event-screen-selected-field-filter eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-4-12 eds-l-sn-pad-left-0 eds-l-sm-pad-left-0 eds-l-pad-left-2"
                            data-spec="event-screen-status-filter-shell"
                        >
                            <SelectField
                                id="event-status-filter"
                                label={gettext('Event status')}
                                value={status}
                                values={SELECT_FIELD_FILTER_OPTIONS}
                                onChange={this._handleStatusFilter}
                                defaultValue={status}
                                borderType="default"
                            />
                        </div>
                        {organizerFilter}
                    </div>
                </div>
                {this._renderContent()}
                {createPhab}
            </div>
        );
    }
}
